import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  ExpandMore,
  Error as ErrorIcon,
  CheckCircle,
  Warning,
  Info,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { ProcessIncident, ProcessActivityApi } from '../types/process';
import { useProcessesService } from '../services/processesService';
import { useTenant } from '../hooks/useTenant';

interface ProcessIncidentsProps {
  incidents: ProcessIncident[];
  incidentsCount: number;
  activities?: ProcessActivityApi[];
}

export const ProcessIncidents: React.FC<ProcessIncidentsProps> = ({
  incidents,
  incidentsCount,
  activities = [],
}) => {
  const { currentTenant } = useTenant();
  const processesService = useProcessesService();
  const [errorDetailsMap, setErrorDetailsMap] = useState<Record<string, string>>({});
  const [loadingDetailsMap, setLoadingDetailsMap] = useState<Record<string, boolean>>({});
  const [errorLoadingMap, setErrorLoadingMap] = useState<Record<string, string>>({});

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getIncidentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'failedJob': 'Falha no Job',
      'failedExternalTask': 'Falha em Tarefa Externa',
      'failedProcessStart': 'Falha ao Iniciar Processo',
      'unhandledBpmnError': 'Erro BPMN Não Tratado',
    };
    return types[type] || type;
  };

  // Função para obter o nome da atividade pelo ID
  const getActivityName = (activityId?: string | null): string | null => {
    if (!activityId) return null;
    const activity = activities.find(a => a.activity_id === activityId);
    return activity?.activity_name || null;
  };

  // Função para buscar detalhes do erro
  const fetchErrorDetails = async (incidentId: string, externalTaskId: string) => {
    if (errorDetailsMap[incidentId]) {
      // Se já carregou, apenas esconde
      setErrorDetailsMap(prev => {
        const newMap = { ...prev };
        delete newMap[incidentId];
        return newMap;
      });
      return;
    }

    if (!currentTenant) {
      setErrorLoadingMap(prev => ({ ...prev, [incidentId]: 'Tenant não selecionado' }));
      return;
    }

    setLoadingDetailsMap(prev => ({ ...prev, [incidentId]: true }));
    setErrorLoadingMap(prev => ({ ...prev, [incidentId]: '' }));

    try {
      const response = await processesService.getIncidentErrorDetails(
        currentTenant.name,
        externalTaskId
      );
      setErrorDetailsMap(prev => ({ ...prev, [incidentId]: response.error_details }));
    } catch (error) {
      console.error('Erro ao buscar detalhes do erro:', error);
      setErrorLoadingMap(prev => ({
        ...prev,
        [incidentId]: error instanceof Error ? error.message : 'Erro ao carregar detalhes'
      }));
    } finally {
      setLoadingDetailsMap(prev => ({ ...prev, [incidentId]: false }));
    }
  };

  const getStatusIcon = (incident: ProcessIncident) => {
    if (incident.resolved) {
      return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    if (incident.open) {
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    }
    return <Warning sx={{ color: 'warning.main', fontSize: 20 }} />;
  };

  const getStatusChip = (incident: ProcessIncident) => {
    if (incident.resolved) {
      return <Chip label="Resolvido" color="success" size="small" />;
    }
    if (incident.open) {
      return <Chip label="Aberto" color="error" size="small" />;
    }
    if (incident.deleted) {
      return <Chip label="Deletado" color="default" size="small" />;
    }
    return <Chip label="Fechado" color="warning" size="small" />;
  };

  // Filtrar incidentes com ID válido para evitar erros
  const validIncidents = incidents.filter(i => i.id);

  if (incidentsCount === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
          <Typography variant="h6" fontWeight={600}>
            Sem Incidentes
          </Typography>
        </Box>
        <Alert severity="success" icon={false}>
          Esta instância não possui nenhum incidente registrado. Tudo está funcionando corretamente! 🎉
        </Alert>
      </Paper>
    );
  }

  const openIncidents = validIncidents.filter(i => i.open);
  const resolvedIncidents = validIncidents.filter(i => i.resolved);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ErrorIcon sx={{ color: 'error.main', fontSize: 32 }} />
        <Typography variant="h6" fontWeight={600}>
          Incidentes ({incidentsCount})
        </Typography>
      </Box>

      {openIncidents.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{openIncidents.length}</strong> incidente(s) aberto(s) requer(em) atenção imediata.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          icon={<ErrorIcon />}
          label={`${openIncidents.length} Aberto(s)`}
          color="error"
          variant={openIncidents.length > 0 ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<CheckCircle />}
          label={`${resolvedIncidents.length} Resolvido(s)`}
          color="success"
          variant={resolvedIncidents.length > 0 ? 'filled' : 'outlined'}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mt: 2 }}>
        {validIncidents.map((incident) => (
          <Accordion
            key={incident.id}
            defaultExpanded={incident.open === true}
            sx={{
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: incident.open ? 3 : 1,
              border: incident.open ? '2px solid' : '1px solid',
              borderColor: incident.open ? 'error.main' : 'divider',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: incident.open ? 'error.light' : 'background.default',
                '&:hover': { backgroundColor: incident.open ? 'error.light' : 'action.hover' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                {getStatusIcon(incident)}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {getIncidentTypeLabel(incident.incident_type || '')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {incident.id}
                  </Typography>
                </Box>
                {getStatusChip(incident)}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {incident.incident_message && (
                  <Alert severity={incident.open ? 'error' : 'info'} icon={<Info />}>
                    <Typography variant="body2">
                      <strong>Mensagem:</strong> {incident.incident_message}
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Criado em
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(incident.create_time)}
                    </Typography>
                  </Box>
                  {incident.end_time && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Resolvido em
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(incident.end_time)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {incident.failed_activity_id && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Atividade que falhou
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {getActivityName(incident.failed_activity_id) && (
                        <>
                          {getActivityName(incident.failed_activity_id)}
                          <Typography component="span" variant="body2" fontFamily="monospace" color="text.secondary" sx={{ ml: 1 }}>
                            ({incident.failed_activity_id})
                          </Typography>
                        </>
                      )}
                      {!getActivityName(incident.failed_activity_id) && (
                        <Typography component="span" variant="body2" fontFamily="monospace">
                          {incident.failed_activity_id}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}

                {incident.annotation && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Anotação
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {incident.annotation}
                    </Typography>
                  </Box>
                )}

                {/* Botão para exibir detalhes do erro */}
                {incident.configuration && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant={errorDetailsMap[incident.id || ''] ? 'outlined' : 'contained'}
                        size="small"
                        startIcon={
                          loadingDetailsMap[incident.id || ''] ? (
                            <CircularProgress size={16} />
                          ) : errorDetailsMap[incident.id || ''] ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )
                        }
                        onClick={() => fetchErrorDetails(incident.id || '', incident.configuration || '')}
                        disabled={loadingDetailsMap[incident.id || '']}
                        color={errorDetailsMap[incident.id || ''] ? 'secondary' : 'primary'}
                      >
                        {errorDetailsMap[incident.id || '']
                          ? 'Ocultar Detalhes do Erro'
                          : 'Exibir Detalhes do Erro (Stacktrace)'}
                      </Button>
                    </Box>
                  </>
                )}

                {/* Detalhes do erro */}
                {incident.configuration && (
                  <Box>

                      {errorLoadingMap[incident.id || ''] && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {errorLoadingMap[incident.id || '']}
                        </Alert>
                      )}

                      <Collapse in={!!errorDetailsMap[incident.id || '']} timeout="auto" unmountOnExit>
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: 'grey.100',
                            border: '1px solid',
                            borderColor: 'error.main',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'error.main' }}>
                            Stacktrace Completo:
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              backgroundColor: 'white',
                              p: 2,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              overflow: 'auto',
                              maxHeight: 400,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {errorDetailsMap[incident.id || '']}
                          </Box>
                        </Paper>
                      </Collapse>
                    </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
};

